/**
 * Analytics Utilities for TENSION App
 * 
 * Privacy-first analytics that tracks user actions without collecting personal data
 * All events are anonymized and aggregated on the server
 */

import { projectId, publicAnonKey } from '/utils/supabase/info';
import { isSupabaseOnline } from './supabase-health';
import { trackTelemetryEvent } from './telemetry';

export type AnalyticsEvent = 
  // Marketing awareness & attribution
  | 'landing_page_viewed'
  | 'utm_attribution_captured'
  | 'campaign_click_tracked'
  | 'ad_impression_tracked'
  | 'ad_click_tracked'

  // Marketing acquisition/auth
  | 'signup_started'
  | 'signup_completed'
  | 'login_succeeded'
  | 'email_verification_completed'

  // Marketing growth loops
  | 'referral_invite_sent'
  | 'referral_signup_completed'
  | 'social_post_published'
  | 'social_review_submitted'
  | 'social_review_approved'
  | 'creator_outreach_sent'
  | 'creator_campaign_launched'
  | 'creator_deal_closed'
  | 'seo_asset_published'
  | 'seo_rank_top10_achieved'
  | 'automation_job_succeeded'
  | 'automation_job_failed'
  | 'ai_marketing_asset_generated'

  // Core actions
  | 'app_opened'
  | 'onboarding_started'
  | 'onboarding_completed'
  | 'relationship_added'
  | 'relationship_switched'
  
  // Check-ins
  | 'check_in_started'
  | 'check_in_completed'
  | 'check_in_abandoned'
  /** Fired once when the user completes their first stored check-in (activation KPI). */
  | 'first_check_in_completed'
  
  // Engagement
  | 'journal_entry_added'
  /** First saved journal entry (activation / depth-of-use KPI). */
  | 'first_journal_entry_completed'
  | 'journal_voice_note_added'
  | 'anxiety_logged'
  | 'anxiety_outcome_added'
  | 'attachment_assessment_taken'
  | 'rumination_intervention_completed'
  | 'therapist_export_generated'
  | 'data_backup_downloaded'
  | 'data_backup_restored'
  | 'cloud_sync_enabled'
  | 'cloud_sync_disabled'
  | 'cloud_sync_manual_triggered'
  | 'support_request_submitted'
  | 'faq_page_viewed'
  | 'support_page_viewed'
  | 'copilot_opened'
  | 'copilot_mode_selected'
  | 'copilot_persona_selected'
  | 'copilot_prompt_submitted'
  | 'copilot_response_received'
  | 'copilot_request_failed'
  | 'copilot_response_feedback'
  | 'login_snapshot_generated'
  | 'login_snapshot_failed'
  /** Atlas Groq returned micro-actions for today. */
  | 'micro_actions_atlas_loaded'
  /** Offline rules used (includes no token, API error, empty response, network). */
  | 'micro_actions_local_fallback'
  | 'admin_snapshot_slo_dashboard_viewed'
  | 'admin_snapshot_slo_preset_applied'
  | 'admin_snapshot_slo_recommended_applied'
  | 'admin_snapshot_slo_report_exported'
  
  // Milestones
  | 'streak_milestone_7'
  | 'streak_milestone_14'
  | 'streak_milestone_30'
  | 'streak_milestone_60'
  | 'rti_state_changed'
  | 'attachment_style_shifted'
  
  // Monetization
  | 'pricing_page_viewed'
  | 'checkout_initiated'
  | 'subscription_activated'
  | 'subscription_cancelled'
  | 'pack_purchased'
  | 'pack_completed'
  
  // Navigation
  | 'timeline_viewed'
  | 'insights_viewed'
  | 'settings_viewed'
  | 'notifications_viewed'
  | 'error_logs_viewed'
  | 'theme_changed'
  | 'performance_mode_changed'

  // Account & profile
  | 'account_deleted'
  | 'account_deletion_initiated'
  | 'account_deleted_successfully'
  | 'account_deletion_failed'
  | 'profile_photo_uploaded'
  | 'profile_updated'
  | 'password_reset_no_account'
  | 'password_reset_requested'
  | 'password_reset_failed'
  | 'password_reset_completed'
  | 'password_reset_update_failed'

  // Community & tips
  | 'tip_submitted'
  | 'community_post_created'
  | 'community_reply_created'
  | 'community_daily_checkin_completed'
  | 'community_room_response_submitted'
  | 'community_accountability_circle_created'
  | 'social_feed_viewed'
  | 'social_post_published'
  | 'social_reaction_sent'
  | 'social_comment_sent'
  | 'social_follow_toggled'
  | 'social_notification_read'
  | 'social_digest_refreshed'
  | 'social_challenge_completed'
  | 'social_coach_prompt_sent'
  | 'social_match_suggestion_opened'
  | 'social_guardrail_auto_rollback'
  | 'social_rollout_access_blocked'
  | 'social_rollout_access_allowed'
  | 'home_core_action_started'
  | 'home_social_panel_toggled'
  | 'home_social_action_taken'
  | 'home_social_gate_blocked'
  | 'home_social_gate_unlocked'
  | 'home_social_handoff_prompt_clicked'
  | 'home_social_unlock_celebrated'
  | 'home_social_mission_started'
  | 'home_social_mission_completed'
  | 'home_social_quality_mode_changed'
  | 'home_social_follow_conversion'
  | 'home_social_friend_search_ranked'
  | 'home_social_weekly_recap_viewed'
  | 'home_social_panel_bootstrapped'
  | 'home_social_momentum_complete_next_step'
  | 'home_tension_realtime_generated'
  | 'ri_section_viewed'
  | 'ri_variant_assigned'
  | 'ri_variant_promoted'
  | 'ri_rollout_blocked'
  | 'ri_rollout_enabled'
  | 'ri_trend_scenario_selected'
  | 'ri_message_style_selected'
  | 'ri_path_committed'
  | 'ri_mission_step_toggled'
  | 'ri_mission_reset'
  | 'ri_upgrade_prompt_clicked'
  | 'ri_interaction_started'
  | 'ri_interaction_completed'
  | 'ri_cta_clicked'

  // Cloud sync (detailed)
  | 'cloud_sync_push_success'
  | 'cloud_sync_push_failed'
  | 'cloud_sync_pull_success'
  | 'cloud_sync_pull_failed'
  | 'cloud_sync_full_success'
  | 'cloud_sync_full_failed'

  // Sessions & security
  | 'session_revoked'
  | 'all_sessions_revoked'
  | 'multiple_sessions_detected'
  | 'cross_platform_sessions'
  | 'user_returned_after_inactivity'
  | 'session_duration_milestone'

  // Data integrity & recovery
  | 'data_corruption_detected'
  | 'data_corruption_partial'
  | 'data_auto_recovered'
  | 'data_recovered'
  | 'data_recovery_failed'
  | 'data_restored_from_backup'
  | 'backup_restore_failed'
  | 'storage_quota_warning'

  // Relationship / break mode
  | 'break_mode_activated'
  | 'break_mode_deactivated'
  | 'relationship_archived'
  | 'relationship_mode_changed'

  // Feedback & sharing
  | 'feedback_submitted'
  | 'share_clicked'

  // Crisis & safety
  | 'crisis_resources_shown'
  | 'crisis_resources_dismissed'
  | 'crisis_resource_clicked'
  | 'safety_assessment_opened'

  // Notifications & reminders
  | 'notification_permission_granted'
  | 'notification_permission_denied'
  | 'missed_checkin_reminder_shown'
  | 'reminder_settings_updated'

  // Push / notification lifecycle (see notification-analytics.ts)
  | 'notification_delivered'
  | 'notification_clicked'
  | 'notification_dismissed'
  | 'notification_enabled'
  | 'notification_disabled'
  | 'notification_time_changed';

export interface AnalyticsEventData {
  event: AnalyticsEvent;
  timestamp: string;
  properties?: Record<string, any>;
}

export interface AnalyticsConfig {
  enabled: boolean;
  batchSize: number;
  flushInterval: number; // ms
}

class Analytics {
  private config: AnalyticsConfig = {
    enabled: true,
    batchSize: 10,
    flushInterval: 30000, // 30 seconds
  };

  private eventQueue: AnalyticsEventData[] = [];
  private flushTimer: NodeJS.Timeout | null = null;
  private authToken: string | null = null;

  /** Set auth token for server-side user identity binding. Call from auth context. */
  setAuthToken(token: string | null): void {
    this.authToken = token;
  }

  constructor() {
    // Check if analytics is enabled in settings
    const analyticsEnabled = localStorage.getItem('tension_analytics_enabled');
    if (analyticsEnabled === 'false') {
      this.config.enabled = false;
    }

    // Start flush timer
    this.startFlushTimer();

    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  /**
   * Track an analytics event
   */
  track(event: AnalyticsEvent, properties?: Record<string, any>): void {
    if (!this.config.enabled) {
      return;
    }

    const eventData: AnalyticsEventData = {
      event,
      timestamp: new Date().toISOString(),
      properties: this.sanitizeProperties(properties),
    };

    // Add to queue
    this.eventQueue.push(eventData);

    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 Analytics Event:', eventData);
    }

    // Flush if batch size reached
    if (this.eventQueue.length >= this.config.batchSize) {
      this.flush();
    }
  }

  /**
   * Sanitize properties to remove PII (Personally Identifiable Information)
   */
  private sanitizeProperties(properties?: Record<string, any>): Record<string, any> | undefined {
    if (!properties) return undefined;

    const sanitized: Record<string, any> = {};

    for (const [key, value] of Object.entries(properties)) {
      // Skip sensitive keys
      if (this.isSensitiveKey(key)) {
        continue;
      }

      // Sanitize string values
      if (typeof value === 'string') {
        sanitized[key] = this.sanitizeString(value);
      } else if (typeof value === 'number' || typeof value === 'boolean') {
        sanitized[key] = value;
      } else if (Array.isArray(value)) {
        sanitized[key] = value.map(v => typeof v === 'string' ? this.sanitizeString(v) : v);
      } else {
        // For objects, recursively sanitize
        sanitized[key] = this.sanitizeProperties(value);
      }
    }

    return sanitized;
  }

  /**
   * Check if key contains sensitive information
   */
  private isSensitiveKey(key: string): boolean {
    const sensitiveKeys = [
      'email',
      'name',
      'phone',
      'address',
      'password',
      'token',
      'secret',
      'key',
      'partnerName',
      'userName',
      'note',
      'journal',
      'message',
      'thought',
      'prediction',
      'outcome',
    ];

    return sensitiveKeys.some(sensitive => 
      key.toLowerCase().includes(sensitive.toLowerCase())
    );
  }

  /**
   * Sanitize string to remove potential PII
   */
  private sanitizeString(value: string): string {
    // For analytics, we only care about categories, not actual content
    // Return a hash or just indicate presence
    if (value.length > 0) {
      return `[${value.length} chars]`;
    }
    return value;
  }

  /**
   * Flush events to server
   */
  async flush(): Promise<void> {
    if (this.eventQueue.length === 0) {
      return;
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    try {
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

      const headers: Record<string, string> = {
        'Authorization': `Bearer ${publicAnonKey}`,
        'Content-Type': 'application/json',
      };
      if (this.authToken) {
        headers['X-User-Token'] = this.authToken;
      }
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-ba6cedc4/analytics/track`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({ events }),
          signal: controller.signal,
        }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        console.warn('Analytics: Server returned error, re-queuing events');
        // Re-queue events if failed
        this.eventQueue.push(...events);
      } else {
        await Promise.allSettled(
          events.map((event) =>
            trackTelemetryEvent(`analytics_${event.event}`, {
              source: 'client_analytics',
              ...(event.properties || {}),
            }),
          ),
        );
        if (process.env.NODE_ENV === 'development') {
          console.log(`✅ Sent ${events.length} analytics events`);
        }
      }
    } catch (error) {
      // Silently fail - analytics should never block the app
      if (process.env.NODE_ENV === 'development') {
        console.warn('Analytics: Failed to send events (will retry):', error.name);
      }
      // Re-queue events if failed
      this.eventQueue.push(...events);
      
      // Limit queue size to prevent memory issues
      if (this.eventQueue.length > 100) {
        this.eventQueue = this.eventQueue.slice(-50); // Keep last 50
      }
    }
  }

  /**
   * Start automatic flush timer
   */
  private startFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }

    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.config.flushInterval);
  }

  /**
   * Enable analytics
   */
  enable(): void {
    this.config.enabled = true;
    localStorage.setItem('tension_analytics_enabled', 'true');
  }

  /**
   * Disable analytics
   */
  disable(): void {
    this.config.enabled = false;
    localStorage.setItem('tension_analytics_enabled', 'false');
    this.eventQueue = [];
  }

  /**
   * Check if analytics is enabled
   */
  isEnabled(): boolean {
    return this.config.enabled;
  }
}

// Singleton instance
export const analytics = new Analytics();

/**
 * Convenience tracking functions
 */

export const trackCheckIn = (state: string, score: number) => {
  analytics.track('check_in_completed', {
    state,
    scoreRange: Math.floor(score / 10) * 10, // 0-9, 10-19, etc.
  });
};

/** Call after persisting to localStorage; `totalCheckInsAfterSave` is checkins.length after push. */
export const trackCheckInSaved = (options: {
  source: 'daily' | 'quick';
  totalCheckInsAfterSave: number;
  rtiScore: number;
}) => {
  const { source, totalCheckInsAfterSave, rtiScore } = options;
  analytics.track('check_in_completed', {
    source,
    rtiScoreBucket: Math.min(100, Math.floor(rtiScore / 10) * 10),
  });
  if (totalCheckInsAfterSave === 1) {
    analytics.track('first_check_in_completed', { source });
    void analytics.flush();
  }
};

/** Call after persisting journal to localStorage; `totalEntriesAfterSave` is entries.length after push. */
export const trackJournalEntrySaved = (options: {
  totalEntriesAfterSave: number;
  hasVoiceNote: boolean;
}) => {
  const { totalEntriesAfterSave, hasVoiceNote } = options;
  analytics.track('journal_entry_added', { hasVoiceNote });
  if (totalEntriesAfterSave === 1) {
    analytics.track('first_journal_entry_completed', { hasVoiceNote });
    void analytics.flush();
  }
};

export const trackMicroActionsAtlasLoaded = (properties: { trigger: string }) => {
  analytics.track('micro_actions_atlas_loaded', properties);
};

export const trackMicroActionsLocalFallback = (properties: {
  reason: 'no_token' | 'api_error' | 'empty_response' | 'network' | 'unknown';
  trigger: string;
}) => {
  analytics.track('micro_actions_local_fallback', properties);
};

export const trackSubscription = (plan: 'premium' | 'pro') => {
  analytics.track('subscription_activated', { plan });
};

export const trackPackPurchase = (packId: string) => {
  analytics.track('pack_purchased', { packId });
};

export const trackStreak = (days: number) => {
  if (days === 7) analytics.track('streak_milestone_7');
  else if (days === 14) analytics.track('streak_milestone_14');
  else if (days === 30) analytics.track('streak_milestone_30');
  else if (days === 60) analytics.track('streak_milestone_60');
};

export const trackRTIStateChange = (from: string, to: string) => {
  analytics.track('rti_state_changed', {
    from,
    to,
    direction: from < to ? 'worse' : 'better',
  });
};

export const trackAnxiety = (accuracy: 'accurate' | 'inaccurate') => {
  analytics.track('anxiety_outcome_added', { accuracy });
};

export const trackAttachmentShift = (from: string, to: string) => {
  analytics.track('attachment_style_shifted', {
    from,
    to,
    isImprovement: to === 'secure',
  });
};

export const trackMarketingFunnel = (params: {
  event:
    | 'landing_page_viewed'
    | 'signup_started'
    | 'signup_completed'
    | 'login_succeeded'
    | 'email_verification_completed'
    | 'campaign_click_tracked'
    | 'ad_click_tracked';
  channel: string;
  campaignId?: string;
  source?: string;
  medium?: string;
}) => {
  analytics.track(params.event, {
    channel: params.channel,
    campaignId: params.campaignId,
    source: params.source,
    medium: params.medium,
  });
};

export const trackMarketingAutomation = (params: {
  success: boolean;
  jobKey: string;
  runMode: 'manual' | 'scheduled';
}) => {
  analytics.track(params.success ? 'automation_job_succeeded' : 'automation_job_failed', {
    jobKey: params.jobKey,
    runMode: params.runMode,
  });
};